import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const RANGES = [[0, "All time"], [90, "Last 90 days"], [30, "Last 30 days"]];
const pct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "—");

const ResultsTable = ({ rows }) => (
  <table className="w-full text-sm mt-4" data-testid="invite-results-table">
    <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
      <tr><th className="p-2">Practice area</th><th className="p-2 text-right">Sent</th><th className="p-2 text-right">Opened</th><th className="p-2 text-right">Joined</th><th className="p-2 text-right">In review</th><th className="p-2 text-right">Join rate</th></tr>
    </thead>
    <tbody className="divide-y divide-border">
      {rows.map((r) => (
        <tr key={r.area} data-testid={`invite-results-row-${r.area}`}>
          <td className="p-2 font-medium">{r.area}</td><td className="p-2 text-right">{r.sent}</td>
          <td className="p-2 text-right">{r.opened} <span className="text-xs text-muted-foreground">({pct(r.opened, r.sent)})</span></td>
          <td className="p-2 text-right">{r.joined}</td><td className="p-2 text-right">{r.in_review}</td>
          <td className="p-2 text-right font-semibold text-accent">{pct(r.joined, r.sent)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const InviteResults = () => {
  const [days, setDays] = useState(0);
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/invites/stats", { params: { days } }).then(({ data }) => setRows(data.areas)).catch(() => setRows([])); }, [days]);
  return (
    <div className="grid-card p-6 mb-10" data-testid="invite-results">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="font-serif text-2xl text-foreground">Invite results by practice area</p>
          <p className="text-sm text-muted-foreground">Sent, opened (visited their join page) and joined (claimed or listed).</p>
        </div>
        <div className="inline-flex rounded-sm border border-border overflow-hidden text-xs font-semibold" data-testid="invite-results-range">
          {RANGES.map(([d, label]) => (
            <button key={d} type="button" onClick={() => setDays(d)} data-testid={`invite-results-range-${d}`}
              className={`px-3 py-1.5 transition-colors ${days === d ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>{label}</button>
          ))}
        </div>
      </div>
      {!rows ? <p className="text-muted-foreground animate-pulse">Loading…</p> : rows.length === 0
        ? <p className="text-muted-foreground py-6" data-testid="invite-results-empty">No invites sent in this period yet.</p>
        : (
          <>
            <div className="h-72" data-testid="invite-results-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="area" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} cursor={{ fill: "hsl(var(--secondary))" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={(i) => ["sent", "opened", "joined"].indexOf(i.dataKey)} />
                  <Bar dataKey="sent" name="Sent" fill="#94a3b8" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="opened" name="Opened" fill="#38bdf8" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="joined" name="Joined" fill="#10b981" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <ResultsTable rows={rows} />
          </>
        )}
    </div>
  );
};
